import { useRouter } from 'next/router'
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from 'react'
import { KeyedMutator } from 'swr'

import { useFetch } from '@/lib/hooks'
import { api, getUrlString } from '@/lib/utils'
import { Product as ProductType } from '@/types'

import { useAuth } from './AuthProvider'

interface ContextInterface {
  basket: any
  basketError: any
  basketLoading: boolean
  basketMutate: KeyedMutator<any> | undefined
  openBasketDrawer: boolean
  openAddedProductModal: ProductType | null
  setOpenAddedProductModal: Dispatch<SetStateAction<ProductType | null>>
  setOpenBasketDrawer: Dispatch<SetStateAction<boolean>>
  cleanBasket: ({ studentUid }: { studentUid?: string }) => Promise<any>
}

const BasketContext = createContext<ContextInterface>({
  basket: null,
  basketError: null,
  basketLoading: false,
  basketMutate: undefined,
  openAddedProductModal: null,
  openBasketDrawer: false,
  setOpenAddedProductModal: () => {},
  setOpenBasketDrawer: () => {},
  cleanBasket: async ({ studentUid }: { studentUid?: string }) => Promise.resolve(null)
})

interface Props {
  children: React.ReactNode
}

export const useBasket = () => useContext(BasketContext)

function useBasketProvider() {
  // Hooks
  const { isAuthenticated } = useAuth() || {}
  const router = useRouter()
  const { site } = router.query || {}
  const siteSlug = getUrlString(site)

  // States
  const [openBasketDrawer, setOpenBasketDrawer] = useState<boolean>(false)
  const [openAddedProductModal, setOpenAddedProductModal] = useState<ProductType | null>(null)

  // Fetch
  const { data, error, isLoading, mutate } = useFetch([
    siteSlug && isAuthenticated ? `/${siteSlug}/basket/` : null
  ])

  // Actions
  const cleanBasket = async ({ studentUid = '' }: { studentUid?: string }) => {
    const response = await api.post(`/${siteSlug}/basket/clean/`, {
      ...(studentUid ? { studentUid } : {})
    })
    mutate?.()
    return response
  }

  // Effects
  useEffect(() => {
    if (!openAddedProductModal) return
    const timeoutId = window.setTimeout(() => {
      setOpenAddedProductModal(null)
    }, 5000)

    return () => window.clearTimeout(timeoutId)
  }, [openAddedProductModal, setOpenAddedProductModal])

  return {
    basket: data,
    basketError: error,
    basketLoading: isLoading,
    basketMutate: mutate,
    openAddedProductModal,
    openBasketDrawer,
    setOpenAddedProductModal,
    setOpenBasketDrawer,
    cleanBasket
  }
}

export default function BasketProvider({ children }: Props) {
  const nav = useBasketProvider()
  return <BasketContext.Provider value={nav}>{children}</BasketContext.Provider>
}
